import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AssignDateModal, DailySheetModal } from "@/components/bottling/BottlingModals";
import { fmtDate, calcGallons, DAILY_CASE_CAP, WINE_GATE_ORDER, FILTER_TYPES } from "@/lib/bottling-constants";
import { SAMPLE_EVENTS, SAMPLE_SKUS, type SampleBottlingEvent, type SampleBottlingSku } from "@/lib/bottling-sample-data";
import { cn } from "@/lib/utils";

const MAX_UNDO = 20;

// ─── Types ────────────────────────────────────────────────────────────────

interface PlannerSku extends SampleBottlingSku {
  eventId: string;
  skuIdx: number;
  client: string;
}

interface DayGroup {
  dateKey: string;
  events: SampleBottlingEvent[];
  skus: PlannerSku[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function getMonthButtons() {
  const result: { key: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    result.push({
      key: d.toISOString().slice(0, 7),
      label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
    });
  }
  return result;
}

function groupByDate(events: SampleBottlingEvent[], skuData: Record<string, SampleBottlingSku[]>, filterMode: string): DayGroup[] {
  let filtered: SampleBottlingEvent[];
  if (filterMode === "active") {
    filtered = events.filter(e => ["submitted", "confirmed", "locked"].includes(e.status));
  } else if (filterMode === "all") {
    filtered = events;
  } else {
    filtered = events.filter(e => e.confirmedDate && e.confirmedDate.startsWith(filterMode));
  }

  const map: Record<string, DayGroup> = {};
  filtered.forEach(evt => {
    const key = evt.confirmedDate || "tbd";
    if (!map[key]) map[key] = { dateKey: key, events: [], skus: [] };
    map[key].events.push(evt);
    map[key].skus.push(
      ...(skuData[evt.id] || []).map((s, idx) => ({ ...s, eventId: evt.id, skuIdx: idx, client: evt.client }))
    );
  });

  return Object.values(map).sort((a, b) =>
    a.dateKey === "tbd" ? -1 : b.dateKey === "tbd" ? 1 : a.dateKey.localeCompare(b.dateKey)
  );
}

function autoArrange(group: DayGroup, setSkuData: React.Dispatch<React.SetStateAction<Record<string, SampleBottlingSku[]>>>) {
  const sorted = [...group.skus].sort((a, b) => {
    const ra = WINE_GATE_ORDER.findIndex(w => (a.wine || "").toLowerCase().includes(w));
    const rb = WINE_GATE_ORDER.findIndex(w => (b.wine || "").toLowerCase().includes(w));
    return (ra === -1 ? 999 : ra) - (rb === -1 ? 999 : rb) || (a.wine || "").localeCompare(b.wine || "");
  });
  const byEvent: Record<string, SampleBottlingSku[]> = {};
  sorted.forEach(s => {
    if (!byEvent[s.eventId]) byEvent[s.eventId] = [];
    byEvent[s.eventId].push(s);
  });
  setSkuData(prev => {
    const next = { ...prev };
    Object.entries(byEvent).forEach(([id, skus]) => (next[id] = skus));
    return next;
  });
}

// ─── ProductionPlanner ────────────────────────────────────────────────────

export default function ProductionPlanner() {
  const [events, setEvents] = useState(SAMPLE_EVENTS);
  const [skuData, setSkuData] = useState(SAMPLE_SKUS);
  const [filterMode, setFilterMode] = useState("active");
  const [showAssign, setShowAssign] = useState(false);
  const [assignEventIds, setAssignIds] = useState<string[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDate, setSheetDate] = useState<string | null>(null);
  const [expandAll, setExpandAll] = useState<boolean | null>(null);
  const [undoStack, setUndoStack] = useState<{ label: string; skuData: Record<string, SampleBottlingSku[]> }[]>([]);

  const groups = groupByDate(events, skuData, filterMode);
  const monthBtns = getMonthButtons();
  const canUndo = undoStack.length > 0;

  const allFilterBtns = [
    { key: "active", label: "Active" },
    ...monthBtns,
    { key: "all", label: "All incl. Completed" },
  ];

  function pushUndo(label: string, prevSkuData: Record<string, SampleBottlingSku[]>) {
    setUndoStack(prev => [{ label, skuData: prevSkuData }, ...prev].slice(0, MAX_UNDO));
  }

  function handleUndo() {
    if (!canUndo) return;
    const [top, ...rest] = undoStack;
    setSkuData(top.skuData);
    setUndoStack(rest);
  }

  function handleAssignDate(date: string) {
    if (!date) { alert("Please select a date."); return; }
    setEvents(prev =>
      prev.map(e => assignEventIds.includes(e.id) ? { ...e, confirmedDate: date, status: "confirmed" as const } : e)
    );
    setShowAssign(false);
  }

  function handlePrint() {
    const body = document.getElementById("daily-sheet-modal-body");
    if (!body) return;
    const win = window.open("", "_blank", "width=1300,height=900");
    if (!win) { alert("Please allow popups to use Print."); return; }
    win.document.write(buildPrintHtml(body.innerHTML, sheetDate ? fmtDate(sheetDate) : ""));
    win.document.close();
  }

  const sheetSkus = sheetDate
    ? events
        .filter(e => e.confirmedDate === sheetDate)
        .flatMap(e => (skuData[e.id] || []).map(s => ({ ...s, client: e.client })))
    : [];

  function handleSkuSave(eventId: string, skuIdx: number, updatedSku: PlannerSku) {
    pushUndo("SKU edit", skuData);
    setSkuData(prev => {
      const next = { ...prev };
      const arr = [...(next[eventId] || [])];
      const { eventId: _e, skuIdx: _i, client: _c, ...cleanSku } = updatedSku;
      arr[skuIdx] = { ...arr[skuIdx], ...cleanSku };
      next[eventId] = arr;
      return next;
    });
  }

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <div className="mb-6 flex items-end justify-between border-b pb-5">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Production Planner</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Confirmed bottling schedule · drag rows to reorder · Auto Arrange by wine gate
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleUndo} disabled={!canUndo}
            title={canUndo ? `Undo: ${undoStack[0]?.label}` : "Nothing to undo"}
          >
            Undo
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setExpandAll(true)}>Expand All</Button>
          <Button variant="ghost" size="sm" onClick={() => setExpandAll(false)}>Collapse All</Button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-1.5">
        {allFilterBtns.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setFilterMode(key); setExpandAll(null); }}
            className={cn(
              "rounded border px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider transition-colors",
              filterMode === key
                ? "border-primary/30 bg-primary/10 text-primary"
                : "border-border bg-transparent text-muted-foreground hover:border-muted-foreground hover:text-foreground"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div id="production-planner-body" className="space-y-3">
        {groups.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">No events to display for this filter.</p>
        ) : (
          groups.map(group => (
            <PlannerDayCard
              key={group.dateKey}
              group={group}
              forceOpen={expandAll}
              onExpandReset={() => setExpandAll(null)}
              onOpenAssign={ids => { setAssignIds(ids); setShowAssign(true); }}
              onOpenSheet={date => { setSheetDate(date); setSheetOpen(true); }}
              onAutoArrange={g => { pushUndo("Auto Arrange", skuData); autoArrange(g, setSkuData); }}
              onSkuSave={handleSkuSave}
            />
          ))
        )}
      </div>

      <AssignDateModal open={showAssign} onClose={() => setShowAssign(false)} onConfirm={handleAssignDate} />
      <DailySheetModal open={sheetOpen} onClose={() => setSheetOpen(false)} onPrint={handlePrint}>
        <DailySheetTable skus={sheetSkus} />
      </DailySheetModal>
    </div>
  );
}

// ─── PlannerDayCard ───────────────────────────────────────────────────────

function PlannerDayCard({
  group, forceOpen, onExpandReset, onOpenAssign, onOpenSheet, onAutoArrange, onSkuSave,
}: {
  group: DayGroup;
  forceOpen: boolean | null;
  onExpandReset: () => void;
  onOpenAssign: (ids: string[]) => void;
  onOpenSheet: (date: string) => void;
  onAutoArrange: (g: DayGroup) => void;
  onSkuSave: (eventId: string, skuIdx: number, updatedSku: PlannerSku) => void;
}) {
  const [open, setOpen] = useState(false);
  const isTbd = group.dateKey === "tbd";
  const totalCases = group.skus.reduce((s, x) => s + Number(x.cases || 0), 0);

  useEffect(() => {
    if (forceOpen !== null) setOpen(forceOpen);
  }, [forceOpen]);

  const dateLabel = isTbd
    ? "Unscheduled — TBD"
    : fmtDate(group.dateKey, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <Card className={cn("overflow-visible", isTbd && "border-2 border-dashed")}>
      <div
        className="flex cursor-pointer select-none items-center justify-between px-5 py-4"
        onClick={() => { onExpandReset(); setOpen(v => !v); }}
      >
        <div>
          <div className={cn("text-xl font-semibold", isTbd && "text-muted-foreground")}>{dateLabel}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted-foreground">
            <span>{[...new Set(group.events.map(e => e.client))].join(", ")}</span>
            <span>·</span>
            <span>{group.skus.length} SKUs</span>
            <span>·</span>
            <span>{totalCases.toLocaleString()} cases</span>
            {!isTbd && <CapBadge cases={totalCases} />}
          </div>
        </div>

        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          {isTbd ? (
            <Button size="sm" onClick={() => onOpenAssign(group.events.map(ev => ev.id))}>Assign Date</Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => onOpenSheet(group.dateKey)}>Daily Sheet</Button>
              <Button variant="ghost" size="sm" onClick={() => onAutoArrange(group)}>Auto Arrange</Button>
              <Button variant="ghost" size="sm" className="text-[11px] text-muted-foreground" onClick={() => onOpenAssign(group.events.map(ev => ev.id))}>Edit Date</Button>
            </>
          )}
          <span className="ml-1 text-sm text-muted-foreground">{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <div className="overflow-x-auto border-t">
          <PlannerSkuTable group={group} onSkuSave={onSkuSave} />
        </div>
      )}
    </Card>
  );
}

// ─── CapBadge ─────────────────────────────────────────────────────────────

function CapBadge({ cases }: { cases: number }) {
  const pct = Math.round((cases / DAILY_CASE_CAP) * 100);
  const cls = pct > 100
    ? "bg-destructive/15 text-destructive border-destructive/30"
    : pct >= 75
    ? "bg-warning/15 text-warning border-warning/30"
    : "bg-success/15 text-success border-success/30";
  return <span className={cn("rounded-full border px-2.5 py-0.5 text-[10px]", cls)}>{pct}% of cap</span>;
}

// ─── Column group definitions ─────────────────────────────────────────────

const COL_GROUPS = [
  { label: "", cols: 2, bg: "bg-muted" },
  { label: "Wine Info", cols: 7, bg: "bg-blue-50 dark:bg-blue-950/30" },
  { label: "Bottle", cols: 6, bg: "bg-green-50 dark:bg-green-950/30" },
  { label: "Treatment", cols: 3, bg: "bg-yellow-50 dark:bg-yellow-950/30" },
  { label: "", cols: 1, bg: "bg-muted" },
];

const SUB_HEADERS: [string, string][] = [
  ["", "bg-muted"],
  ["#", "bg-muted"],
  ["Client", "bg-blue-50 dark:bg-blue-950/30"],
  ["Wine", "bg-blue-50 dark:bg-blue-950/30"],
  ["Gals", "bg-blue-50 dark:bg-blue-950/30"],
  ["Cases", "bg-blue-50 dark:bg-blue-950/30"],
  ["Fanciful", "bg-blue-50 dark:bg-blue-950/30"],
  ["AVA", "bg-blue-50 dark:bg-blue-950/30"],
  ["Lot", "bg-blue-50 dark:bg-blue-950/30"],
  ["Vintage", "bg-green-50 dark:bg-green-950/30"],
  ["Type", "bg-green-50 dark:bg-green-950/30"],
  ["Format", "bg-green-50 dark:bg-green-950/30"],
  ["Pack", "bg-green-50 dark:bg-green-950/30"],
  ["Closure", "bg-green-50 dark:bg-green-950/30"],
  ["Label", "bg-green-50 dark:bg-green-950/30"],
  ["Cap", "bg-yellow-50 dark:bg-yellow-950/30"],
  ["Filter", "bg-yellow-50 dark:bg-yellow-950/30"],
  ["DMDC", "bg-yellow-50 dark:bg-yellow-950/30"],
  ["", "bg-muted"],
];

// ─── PlannerSkuTable ──────────────────────────────────────────────────────

function PlannerSkuTable({ group, onSkuSave }: { group: DayGroup; onSkuSave: (eventId: string, skuIdx: number, updatedSku: PlannerSku) => void }) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<PlannerSku | null>(null);

  function startEdit(sku: PlannerSku) {
    setEditingKey(`${sku.eventId}-${sku.skuIdx}`);
    setDraft({ ...sku });
  }
  function cancelEdit() { setEditingKey(null); setDraft(null); }
  function saveEdit() { if (draft) { onSkuSave(draft.eventId, draft.skuIdx, draft); cancelEdit(); } }
  function setF(field: string, val: any) { setDraft(d => d ? { ...d, [field]: val } : d); }

  return (
    <table className="min-w-[1300px] w-full border-collapse text-sm">
      <thead>
        <tr>
          {COL_GROUPS.map((g, i) => (
            <th key={i} colSpan={g.cols} className={cn(g.bg, "px-4 py-1.5 text-center text-[9px] font-medium uppercase tracking-wider text-muted-foreground")}>
              {g.label}
            </th>
          ))}
        </tr>
        <tr>
          {SUB_HEADERS.map(([h, bg], i) => (
            <th key={i} className={cn("border-b px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap", bg)}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {group.skus.map((sku, i) => {
          const key = `${sku.eventId}-${sku.skuIdx}`;
          const isEditing = editingKey === key;
          const row = isEditing ? draft! : sku;
          const gals = calcGallons(row.cases, row.casePack, row.format);
          const b = "border-b group-last:border-b-0";

          return (
            <tr key={i} className="group" draggable={!isEditing}>
              <td className={cn(b, "cursor-grab px-2 py-2.5 text-center text-[13px] text-muted-foreground")}>⠿</td>
              <td className={cn(b, "px-3 py-2.5 text-[11px] text-muted-foreground bg-muted")}>{i + 1}</td>
              <td className={cn(b, "px-3 py-2.5 text-[12px] text-muted-foreground bg-blue-50 dark:bg-blue-950/30")}>{sku.client}</td>
              <td className={cn(b, "px-3 py-2.5 bg-blue-50 dark:bg-blue-950/30")}>
                <strong className="text-[12px]">{row.wine}</strong>
                <br /><span className="text-[11px] text-muted-foreground">{row.brand}</span>
              </td>
              <td className={cn(b, "px-3 py-2.5 text-right font-mono text-[12px] text-muted-foreground bg-blue-50 dark:bg-blue-950/30")}>{gals}</td>
              <td className={cn(b, "px-3 py-2.5 text-right font-mono font-semibold bg-blue-50 dark:bg-blue-950/30")}>
                {isEditing
                  ? <input type="number" className="w-16 rounded border border-border px-1.5 py-1 text-right text-sm font-mono focus:border-primary focus:outline-none" value={draft!.cases} onChange={e => setF("cases", +e.target.value)} />
                  : row.cases}
              </td>
              <td className={cn(b, "px-3 py-2.5 text-[12px] bg-blue-50 dark:bg-blue-950/30")}>
                {isEditing
                  ? <input className="w-28 rounded border border-border px-1.5 py-1 text-sm focus:border-primary focus:outline-none" value={draft!.fanciful || ""} onChange={e => setF("fanciful", e.target.value)} />
                  : <span className="text-muted-foreground">{row.fanciful || "—"}</span>}
              </td>
              <td className={cn(b, "px-3 py-2.5 text-[12px] bg-blue-50 dark:bg-blue-950/30")}>
                {isEditing
                  ? <input className="w-28 rounded border border-border px-1.5 py-1 text-sm focus:border-primary focus:outline-none" value={draft!.ava || ""} onChange={e => setF("ava", e.target.value)} />
                  : <span className="text-muted-foreground">{row.ava || "—"}</span>}
              </td>
              <td className={cn(b, "px-3 py-2.5 font-mono text-[11px] bg-blue-50 dark:bg-blue-950/30")}>
                {isEditing
                  ? <input className="w-24 rounded border border-border px-1.5 py-1 text-sm font-mono focus:border-primary focus:outline-none" value={draft!.lotCode || ""} onChange={e => setF("lotCode", e.target.value)} />
                  : row.lotCode || "—"}
              </td>
              <td className={cn(b, "px-3 py-2.5 text-[12px] text-muted-foreground bg-green-50 dark:bg-green-950/30")}>{row.vintage}</td>
              <td className={cn(b, "px-3 py-2.5 text-[12px] text-muted-foreground bg-green-50 dark:bg-green-950/30")}>{row.bottle}</td>
              <td className={cn(b, "px-3 py-2.5 text-[12px] text-muted-foreground bg-green-50 dark:bg-green-950/30")}>{row.format}</td>
              <td className={cn(b, "px-3 py-2.5 text-center text-[12px] text-muted-foreground bg-green-50 dark:bg-green-950/30")}>{row.casePack || 12}</td>
              <td className={cn(b, "px-3 py-2.5 text-[12px] text-muted-foreground bg-green-50 dark:bg-green-950/30")}>{row.closure}</td>
              <td className={cn(b, "px-3 py-2.5 text-center text-[12px] text-muted-foreground bg-green-50 dark:bg-green-950/30")}>{row.label || "Yes"}</td>
              <td className={cn(b, "px-3 py-2.5 text-center text-[12px] text-muted-foreground bg-yellow-50 dark:bg-yellow-950/30")}>{row.capsule || "No"}</td>
              <td className={cn(b, "px-3 py-2.5 text-[12px] bg-yellow-50 dark:bg-yellow-950/30")}>
                {isEditing
                  ? <select className="rounded border border-border bg-background px-1.5 py-1 text-sm focus:border-primary focus:outline-none" value={draft!.filter || ""} onChange={e => setF("filter", e.target.value)}>
                      <option value="">—</option>
                      {FILTER_TYPES.map(f => <option key={f}>{f}</option>)}
                    </select>
                  : <span className="text-muted-foreground">{row.filter || "—"}</span>}
              </td>
              <td className={cn(b, "px-3 py-2.5 text-center text-[12px] bg-yellow-50 dark:bg-yellow-950/30")}>
                {isEditing
                  ? <select className="rounded border border-border bg-background px-1.5 py-1 text-sm focus:border-primary focus:outline-none" value={draft!.dmdc || "No"} onChange={e => setF("dmdc", e.target.value)}>
                      <option>No</option><option>Yes</option>
                    </select>
                  : <span className="text-muted-foreground">{row.dmdc || "No"}</span>}
              </td>
              <td className={cn(b, "sticky right-0 bg-background px-3 py-2.5 shadow-[-2px_0_4px_rgba(0,0,0,0.06)]")}>
                {isEditing ? (
                  <div className="flex gap-1.5">
                    <button onClick={saveEdit} className="rounded bg-primary px-2 py-1 text-[11px] text-primary-foreground hover:opacity-90">Save</button>
                    <button onClick={cancelEdit} className="rounded border border-border px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => startEdit(sku)} className="rounded p-1 text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground" title="Edit this row">✎</button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── DailySheetTable ──────────────────────────────────────────────────────

function DailySheetTable({ skus }: { skus: (SampleBottlingSku & { client: string })[] }) {
  if (!skus.length) return <p className="py-5 text-center text-sm text-muted-foreground">No SKUs for this date.</p>;
  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr>
          {["#", "Client", "Wine", "Gals", "Cases", "Fanciful", "Lot Code", "Type", "Format", "Pack", "Closure", "Filter", "DMDC"].map((h, i) => (
            <th key={i} className={cn(
              "border-b px-2.5 py-2 text-left text-[9px] font-medium uppercase tracking-wider text-muted-foreground",
              i <= 6 ? "bg-blue-50 dark:bg-blue-950/30" : i <= 10 ? "bg-green-50 dark:bg-green-950/30" : "bg-yellow-50 dark:bg-yellow-950/30"
            )}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {skus.map((s, i) => {
          const gals = calcGallons(s.cases, s.casePack, s.format);
          return (
            <tr key={i} className="group">
              <td className="border-b group-last:border-b-0 px-2.5 py-2 font-mono text-muted-foreground bg-blue-50 dark:bg-blue-950/30">{i + 1}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 bg-blue-50 dark:bg-blue-950/30">{s.client}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 bg-blue-50 dark:bg-blue-950/30"><strong>{s.wine}</strong><br /><span className="text-[10px] text-muted-foreground">{s.brand}</span></td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 text-right font-mono text-muted-foreground bg-blue-50 dark:bg-blue-950/30">{gals}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 text-right font-mono font-bold bg-blue-50 dark:bg-blue-950/30">{s.cases}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 text-muted-foreground bg-blue-50 dark:bg-blue-950/30">{s.fanciful || "—"}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 font-mono bg-blue-50 dark:bg-blue-950/30">{s.lotCode || "—"}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 bg-green-50 dark:bg-green-950/30">{s.bottle}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 bg-green-50 dark:bg-green-950/30">{s.format}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 bg-green-50 dark:bg-green-950/30">{s.casePack || 12}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 bg-green-50 dark:bg-green-950/30">{s.closure}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 bg-yellow-50 dark:bg-yellow-950/30">{s.filter || "—"}</td>
              <td className="border-b group-last:border-b-0 px-2.5 py-2 text-center bg-yellow-50 dark:bg-yellow-950/30">{s.dmdc || "No"}</td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={4} className="border-t-2 bg-muted px-2.5 py-2 font-mono uppercase text-muted-foreground">
            Total · {skus.length} SKUs
          </td>
          <td className="border-t-2 bg-muted px-2.5 py-2 font-mono font-bold">
            {skus.reduce((s, x) => s + Number(x.cases || 0), 0).toLocaleString()}
          </td>
          <td colSpan={8} className="border-t-2 bg-muted" />
        </tr>
      </tfoot>
    </table>
  );
}

// ─── Print HTML builder ───────────────────────────────────────────────────

function buildPrintHtml(tableHtml: string, dateLabel: string): string {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>GWH Bottling Sheet — ${dateLabel}</title>
  <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:system-ui,-apple-system,sans-serif;font-size:11px;color:#1a1a1a;padding:28px 36px}
  .hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid #333}
  h1{font-size:20px;font-weight:600}
  table{width:100%;border-collapse:collapse;font-size:10px}
  th{font-size:8px;text-transform:uppercase;letter-spacing:.07em;color:#6B7280;padding:4px 7px;border-bottom:1px solid #D1D5DB;white-space:nowrap}
  td{padding:6px 7px;border-bottom:1px solid #E5E7EB}
  tbody tr:nth-child(even){background:#FAFAFA}
  .footer{margin-top:20px;padding-top:10px;border-top:1px solid #E5E7EB;display:flex;justify-content:space-between;font-size:8px;color:#9CA3AF;text-transform:uppercase}
  button{display:none!important}@media print{@page{margin:.4in;size:landscape}}</style></head>
  <body><div class="hdr"><div><h1>Daily Bottling Sheet</h1><p style="font-size:9px;color:#6B7280;text-transform:uppercase;letter-spacing:.1em">Gravity Wine House · Internal Production Document</p></div>
  <div style="text-align:right;font-size:9px;color:#6B7280"><strong style="font-size:13px;color:#1a1a1a;display:block">${dateLabel}</strong>Printed: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}<br>3354 Coffey Lane, Santa Rosa, CA</div></div>
  ${tableHtml}<div class="footer"><span>Gravity Wine House — Confidential</span><span>Gravity Assist · Bottling Scheduler</span></div>
  <script>window.onload=()=>setTimeout(()=>window.print(),400)<\/script></body></html>`;
}
